import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, type Entity } from "./api";
import { useMe } from "./session";

interface Ctx {
  entities: Entity[];
  current: Entity | null;
  setCurrentId: (id: string) => void;
  reload: () => Promise<void>;
  loading: boolean;
}
const EntityCtx = createContext<Ctx | null>(null);
export const useEntities = () => {
  const c = useContext(EntityCtx);
  if (!c) throw new Error("useEntities fora do EntityProvider");
  return c;
};

const KEY = (tenant: string) => `mm.entity.${tenant}`;
const readKey = (tenant: string) => { try { return localStorage.getItem(KEY(tenant)); } catch { return null; } };

/** Entidade em uso: no PF é a própria pessoa; no PJ, a empresa escolhida (apenas as que o usuário pode acessar). */
export function EntityProvider({ children }: { children: ReactNode }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const tenant = me.tenant?.id ?? "";
  const [entities, setEntities] = useState<Entity[]>([]);
  const [currentId, setCurrent] = useState<string | null>(() => readKey(tenant));
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try { setEntities(await api<Entity[]>("GET", `/api/${realm}/entities`)); } finally { setLoading(false); }
  }, [realm]);
  useEffect(() => { void reload(); }, [reload]);

  const active = useMemo(() => entities.filter((e) => e.status === "ativa"), [entities]);
  const current = useMemo(() => active.find((e) => e.id === currentId) ?? active[0] ?? null, [active, currentId]);
  const setCurrentId = useCallback((id: string) => {
    setCurrent(id);
    try { localStorage.setItem(KEY(tenant), id); } catch { /* preferência local opcional */ }
  }, [tenant]);

  return <EntityCtx.Provider value={{ entities, current, setCurrentId, reload, loading }}>{children}</EntityCtx.Provider>;
}
